import React, { useState, useEffect } from "react";
import { message } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import API from "api/endpoints";
import { Link } from "react-router-dom";
import Helper from "Pages/EmployeeDashboard/Complaints/Helper";
import axiosInstance from "api/axiosInstance";
import {
  ComplaintStatusEnum,
  ComplaintStatusLabel,
} from "Constants/Enums/Complaint/ComplaintStatusEnum";
import { ComplaintTypeLabel } from "Constants/Enums/Complaint/ComplaintTypeEnum";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

function useComplaints() {
  const navigate = useNavigate();
  const employeeId = localStorage.getItem("personalId");
  const [isLoading, setIsLoading] = useState(false);
  const [show, setShow] = useState(true);
  const [employeeDetails, setEmployeeDetails] = useState([]);
  const [loggedInEmployeeName, setLoggedInEmployeeName] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [complaintTypes, setComplaintTypes] = useState([]);
  const {
    formData,
    formDataError,
    setFormDataError,
    clearFields,
    setFormData,
    validateValidation,
  } = Helper();

  useEffect(() => {
    fetchData();
    fetchDepartments();
    fetchEmployeeByEmail();
    fetchComplaintTypes();
    fetchDepartmentMembers(null);
  }, []);

  const handleComplaints = () => {
    setShow(false);
  };

  const handleSubmitComplaints = async () => {
    try {
      //  Step 1: Validate
      const isValid = validateValidation(formData, setFormDataError);

      if (!isValid) {
        message.error("Please fill in all required fields.");

        return;
      }

      //  Step 2: Build Payload
      const payload = {
        complaintAgainstEmployeeId: Number(formData.complaintAgainstEmployeeId),
        complaintTypeId: Number(formData.complaintTypeId),
        complaintSubTypeId:
          Number(formData.complaintTypeId) === 0 && formData.complaintSubTypeId
            ? Number(formData.complaintSubTypeId)
            : null, // only if type is 0, else null
        otherComplaintTypeText: formData.otherComplaintTypeText || "",
        timeOfIncident: formData.timeOfIncident
          ? new Date(formData.timeOfIncident).toISOString()
          : new Date().toISOString(),
        detailOfIncident: formData.detailOfIncident,
      };
      setIsLoading(true);

      //  Step 3: API Call
      const response = await axiosInstance.post(API.complaint.create, payload);

      if (response.status === 200 || response.status === 201) {
        message.success("Complaint submitted successfully!");
        clearFields();
        fetchData();
        setShow(true);
      } else {
        message.error("Failed to submit complaint. Please try again.");
      }
    } catch (error) {
      console.error(" Error submitting complaint:", error);
      message.error("An error occurred while submitting the complaint.");
    } finally {
      setIsLoading(false);
    }
  };

  async function fetchComplaintTypes() {
    try {
      const response = await axiosInstance.get(API.complaintType.listWithSubTypes);

      if (Array.isArray(response.data)) {
        setComplaintTypes(response.data);
      } else {
        console.warn("Unexpected response format for complaint types:", response.data);
        setComplaintTypes([]);
      }
    } catch (error) {
      console.error(" Error fetching complaint types:", error);
      message.error("Failed to load complaint types. Please try again.");
    }
  }

  async function fetchData() {
    try {
      const response = await axiosInstance.get(API.complaint.getMyComplaint);
      const complaints = Array.isArray(response.data) ? response.data : [];

      const updatedRows = complaints.map((info) => ({
        key: info.id?.toString(),
        ComplaintTitle: info.complaintTypeName,
        ComplaintAgainst: info.complaintAgainstFirstName
          ? `${info.complaintAgainstFirstName} ${info.complaintAgainstLasttName || ""}`
          : `User ID: ${info.complaintAgainstEmployeeId}`,
        Status: ComplaintStatusLabel[info.status],
        Action: (
          <span style={{ display: "flex", alignItems: "center" }}>
            <Link to={`/employeedashboard/empcomplaintDetail/${info.id}`}>
              <EyeOutlined className="eyeOutlined" />
            </Link>
          </span>
        ),
      }));

      setDataSource(updatedRows);
    } catch (error) {
      console.error("Error fetching ...........data:", error);
      message.error("Error fetching complaints");
    }
  }

  async function fetchDepartments() {
    const response = await axiosInstance.get(API.department.getList);
    setDepartments(response.data);
  }

  const handleChange = async (e) => {
    const { name, value } = e.target;

    // Department change → fetch department members or all employees
    if (name === "departmentId") {
      const processedValue = value ? Number(value) : null;

      // Fetch members — all if null, department-specific if ID
      fetchDepartmentMembers(processedValue);

      setFormData((prev) => ({
        ...prev,
        departmentId: processedValue,
        complaintAgainstEmployeeId: "",
        complaintAgainstName: "",
      }));

      return;
    }

    // Complaint Against search
    if (name === "complaintAgainstName") {
      const searchValue = String(value);
      setFormData((prev) => ({
        ...prev,
        complaintAgainstName: searchValue,
      }));

      const match = employeeDetails.find((emp) =>
        emp.employeeName.toLowerCase().includes(searchValue.toLowerCase())
      );

      if (match) {
        setFormData((prev) => ({
          ...prev,
          complaintAgainstEmployeeId: match.EmployeeId,
          departmentId: match.departmentId || prev.departmentId,
          departmentName: match.departmentName || prev.departmentName,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          complaintAgainstEmployeeId: "",
        }));
      }

      return;
    }

    // 🔹 Complaint Type → force number
    if (name === "complaintTypeId") {
      const processedValue = Number(value); // convert to number
      setFormData((prev) => ({
        ...prev,
        [name]: processedValue,
      }));
      return;
    }

    // Default handling for other fields
    const processedValue = typeof value === "string" ? value : String(value ?? "");

    let updatedFormData = { ...formData, [name]: processedValue };
    let updatedFormDataError = { ...formDataError };

    if (
      (!processedValue || (typeof processedValue === "string" && processedValue.trim() === "")) &&
      name !== "complaintAgainstName" &&
      name !== "departmentId"
    ) {
      updatedFormDataError[name + "Error"] = `${name} is required.`;
    } else {
      updatedFormDataError[name + "Error"] = "";
    }

    setFormDataError(updatedFormDataError);
    setFormData(updatedFormData);
  };

  const columns = [
    {
      title: "Complaint Title ",
      dataIndex: "ComplaintTitle",
      key: "ComplaintTitle",
    },
    {
      title: "Complaint Against  Name",
      dataIndex: "ComplaintAgainst",
      key: "ComplaintAgainst",
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
    },
    {
      title: "Action",
      dataIndex: "Action",
      key: "Action",
    },
  ];

  async function fetchEmployeeByEmail() {
    try {
      if (employeeId) {
        const response = await axiosInstance.get(API.employee.employeeGetById(employeeId));
        const responseData = response.data;

        if (responseData) {
          // Extract department information safely
          const departmentId = responseData.departmentId || responseData.department?.id;
          const departmentName =
            responseData.department?.name ||
            (typeof responseData.department === "string" ? responseData.department : "");

          setFormData((prevFormData) => ({
            ...prevFormData,

            firstName: responseData.firstName,
            lastName: responseData.lastName,
            phoneNo: responseData.mobileno,
            email: responseData.email || responseData.workEmail || "",
            EmployeeId: Number(responseData.EmployeeId) || 0,
            departments: departmentName,
            departmentId: null,
            complaintAgainst: "",
          }));
        } else {
          console.error("Employee details not found");
        }
      } else {
        console.error("Employee ID not found in local storage");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }

  async function fetchDepartmentMembers(departmentId) {
    try {
      let response;

      //  Always call function
      response = await axiosInstance.get(API.employee.getAllOverview(departmentId));

      const employees = Array.isArray(response.data) ? response.data : [];

      const updated = employees.map((emp) => ({
        EmployeeId: emp.id,
        employeeName: emp.fullName,
        departmentId: emp.departmentId || null,
      }));

      setEmployeeDetails(updated);
    } catch (err) {
      console.error("Error fetching dept members:", err);
    }
  }

  return {
    isLoading,
    show,
    employeeDetails,
    loggedInEmployeeName,
    dataSource,
    columns,
    handleChange,
    handleSubmitComplaints,
    handleComplaints,
    formData,
    formDataError,
    setFormData,
    departments,
    complaintTypes,
  };
}

export default useComplaints;
